import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {TriggerCorrelation, TriggerExpression} from '@models/adminstration/machine/trigger/trigger-expression';
import {ExpressionSignal} from '@models/adminstration/machine/trigger/expression-signal';
import {KeyValuePair} from '@models/adminstration/machine/trigger/key-value-pair';
import {TriggerExpressionTypes} from '@constants/administration/machine/trigger-expression-type';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {TriggerExpressionOperators} from '@constants/administration/machine/trigger-expression-operators';
import {CORRELATIONS} from '@constants/administration/machine/machine-constants';
import {TriggerConfigurationService} from '@services/administration/machine/trigger/trigger-configuration.service';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'scitis-admin-trigger-signal-expression-table',
    templateUrl: './trigger-signal-expression-table.component.html',
    styleUrls: ['./trigger-signal-expression-table.component.scss'],
    standalone: false
})
export class TriggerSignalExpressionTableComponent implements OnChanges {

  @Input()
  isAdminOrHigher = false;
  @Input()
  gatewaySelected: boolean = false;

  @Input()
  triggerSignalExpressions: TriggerExpression[] = [];

  @Input()
  signals: ExpressionSignal[] = [];

  @Output()
  updatedTriggerExpressions: EventEmitter<TriggerExpression[]> = new EventEmitter<TriggerExpression[]>();

  @Output()
  deletedTriggerSignalExpression: EventEmitter<TriggerExpression> = new EventEmitter<TriggerExpression>();

  triggerExpressionTypes: string[] = [];
  correlations: string[] = [];
  operators: KeyValuePair[] = [];
  triggerExpressionTypesEnum = TriggerExpressionTypes;
  form: UntypedFormGroup = new UntypedFormGroup({});
  atLeastOneValue = false;
  signalsLoading: BehaviorSubject<boolean>;

  private equal: KeyValuePair = {
    key: 'EQUAL',
    value: TriggerExpressionOperators.EQUAL
  };
  private notEqual: KeyValuePair = {
    key: 'NOT_EQUAL',
    value: TriggerExpressionOperators.NOT_EQUAL
  };
  private greater: KeyValuePair = {
    key: 'GREATER',
    value: TriggerExpressionOperators.GREATER
  };
  private less: KeyValuePair = {
    key: 'LESS',
    value: TriggerExpressionOperators.LESS
  };

  constructor(private triggerConfigurationService: TriggerConfigurationService) {
    this.signalsLoading = triggerConfigurationService.getSignalsLoadingSubject();
  }

  get formControls() {
    return this.form.controls;
  }


  ngOnChanges() {
    this.initialize();
  }

  initialize(): void {
    this.setTriggerExpressionTypes();
    this.setCorrelations();
    this.generateForm();
  }

  onRemoveExpression(index: number, expression: TriggerExpression): void {
    this.triggerSignalExpressions.splice(index, 1);
    this.updatedTriggerExpressions.emit(this.triggerSignalExpressions);
    this.deletedTriggerSignalExpression.emit(expression);
  }

  onAddExpression(): void {
    if (this.form.valid) {
      const newTriggerExpression = new TriggerExpression();
      newTriggerExpression.triggerCorrelation = this.formControls.correlation.value;
      if (!newTriggerExpression.triggerCorrelation) {
        newTriggerExpression.triggerCorrelation = TriggerCorrelation.NONE;
      }
      newTriggerExpression.triggerExpressionType = this.formControls.expressionType.value;
      newTriggerExpression.arithmeticOperator = this.formControls.operator.value;
      newTriggerExpression.triggerLastValue = this.formControls.lastValue.value;

      this.signals.forEach(signal => {
        if (signal.signalName === this.formControls.signal.value) {
          newTriggerExpression.triggerColumnName = signal.signalColumn;
          newTriggerExpression.triggerTableName = signal.signalTable;
          newTriggerExpression.signalLabel = signal.signalLabel;
          newTriggerExpression.signalUnit = signal.signalUnit || '';
        }
      });

      this.triggerSignalExpressions.push(newTriggerExpression);
      this.updatedTriggerExpressions.emit(this.triggerSignalExpressions);
      this.setCorrelations();
      this.resetFormFields();
    }
  }

  addExpressionDisabled(): boolean {
    return !this.isAdminOrHigher || !this.form.valid || !this.atLeastOneValue;
  }

  onTriggerExpressionTypeChange(selectedExpressionType: string): void {
    switch (selectedExpressionType) {
      case String(TriggerExpressionTypes.NULLEVALUATION): {
        this.operators.push(this.equal);
        this.operators.push(this.notEqual);
        break;
      }
      case String(TriggerExpressionTypes.CONDITION): {
        this.operators.push(this.greater);
        this.operators.push(this.less);
        this.operators.push(this.equal);
        this.operators.push(this.notEqual);
        break;
      }
      default: {
        this.operators = [];
        break;
      }
    }
  }

  onFocusOut() {
    this.setFormValidation();
    this.setAtLeastOneInputFieldHasValue();
  }

  getOperatorSymbol(operatorName: string | undefined | null): string {
    return this.triggerConfigurationService.getOperatorSymbol(operatorName);
  }

  setAtLeastOneInputFieldHasValue() {
    const hasValue = this.formControls.correlation.value || this.formControls.expressionType.value || this.formControls.signal.value || this.formControls.operator.value || this.formControls.lastValue.value;
    this.atLeastOneValue = hasValue;
    return hasValue;
  }

  private generateForm(): void {
    this.form = new UntypedFormGroup({
      correlation: new UntypedFormControl({value: '', disabled: !this.isAdminOrHigher}),
      expressionType: new UntypedFormControl({value: '', disabled: !this.isAdminOrHigher}),
      signal: new UntypedFormControl({value: null, disabled: !this.isAdminOrHigher || !this.gatewaySelected}),
      operator: new UntypedFormControl({value: null, disabled: !this.isAdminOrHigher}),
      lastValue: new UntypedFormControl({value: null, disabled: !this.isAdminOrHigher})
    });

    this.setFormValidation();
  }

  private resetFormFields(): void {
    this.form.reset();
    this.formControls.correlation.setValue('');
    this.formControls.expressionType.setValue('');
    this.formControls.signal.setValue('');
    this.formControls.operator.setValue(null);
    this.formControls.lastValue.setValue(null);

    this.setFormValidation();
  }

  private setTriggerExpressionTypes(): void {
    this.triggerExpressionTypes = [];
    for (const type in TriggerExpressionTypes) {
      if (type === TriggerExpressionTypes.TIME_EVAL) {
        return;
      }
      this.triggerExpressionTypes.push(type);
    }
  }

  private setCorrelations(): void {
    if (this.triggerSignalExpressions.length === 0) {
      this.correlations = [];
    } else {
      this.correlations = CORRELATIONS;
    }
  }

  private setFormValidation(): void {
    if (this.setAtLeastOneInputFieldHasValue()) {
      this.form.get('expressionType')?.setValidators([Validators.required]);
      this.form.get('signal')?.setValidators([Validators.required]);
      if (this.triggerSignalExpressions.length > 0) {
        this.form.get('correlation')?.setValidators([Validators.required]);
      } else {
        this.form.get('correlation')?.setValidators(null);
      }
    } else {
      this.form.get('expressionType')?.setValidators(null);
      this.form.get('signal')?.setValidators(null);
    }

    if (this.form.controls.expressionType.value && this.form.controls.expressionType.value !== String(TriggerExpressionTypes.VALUE_CHANGE)) {
      this.form.get('operator')?.setValidators([Validators.required]);
    } else {
      this.form.get('operator')?.setValidators(null);
    }

    this.form.get('expressionType')?.updateValueAndValidity();
    this.form.get('signal')?.updateValueAndValidity();
    this.form.get('correlation')?.updateValueAndValidity();
    this.form.get('operator')?.updateValueAndValidity();
  }
}
